import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { t } from "i18next";
import { FtlOption } from "../../shared/interfaces/GameLogicTypes";
import { renderCueContent } from "../../shared/helper/renderCueContent";
import "./FtlReveal.css";

export interface FtlRevealProps {
  options: FtlOption[];
  czarChoice: number;
  playerChoice: number | null;
  isCzar: boolean;
  isAlive: boolean;
  eliminations: string[];
  playerId: string;
  onAnimationComplete: () => void;
}

export default function FtlReveal({
  options,
  czarChoice,
  playerChoice,
  isCzar,
  isAlive,
  eliminations,
  playerId,
  onAnimationComplete,
}: FtlRevealProps) {
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    // Start flip after brief delay
    const flipTimer = setTimeout(() => setFlipped(true), 300);
    // Animation complete callback
    const completeTimer = setTimeout(() => onAnimationComplete(), 3000);
    return () => {
      clearTimeout(flipTimer);
      clearTimeout(completeTimer);
    };
  }, []);

  const wasEliminated = eliminations.includes(playerId);
  const gridClass = `ftl-reveal-grid options-${options.length}`;

  const statusMessage = isCzar
    ? t("ftl_leader_has_chosen")
    : wasEliminated
    ? t("ftl_you_were_eliminated")
    : t("ftl_you_survived");

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" textAlign="center">
        {statusMessage}
      </Typography>
      <div className={gridClass}>
        {options.map((option) => {
          // czarChoice === 0 means czar didn't pick — all cards are correct
          const isCorrect = czarChoice === 0 || option.index === czarChoice;
          return (
            <div key={option.index} className="ftl-card-flip">
              <div className={`ftl-card-flip-inner${flipped ? " flipped" : ""}`}>
                {/* Front: the option with its color */}
                <div
                  className="ftl-card-front"
                  style={{
                    backgroundColor: option.color,
                    border:
                      playerChoice === option.index
                        ? "3px solid #ffffff"
                        : "3px solid transparent",
                    boxShadow:
                      playerChoice === option.index
                        ? "0 0 12px rgba(255,255,255,0.5)"
                        : "none",
                  }}
                >
                  {typeof option.cue.content === "string" ? renderCueContent(option.cue.content) : ""}
                </div>
                {/* Back: crown (correct) or skull (wrong) */}
                <div className={`ftl-card-back ${isCorrect ? "correct" : "wrong"}`}>
                  {isCorrect ? (
                    <EmojiEventsIcon sx={{ fontSize: 48, color: "#fff355" }} />
                  ) : (
                    <span role="img" aria-label="skull" style={{ fontSize: "2.5rem" }}>
                      &#128128;
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </Stack>
  );
}
